class PtcProductPage extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ptcReady === 'true') return;
    this.dataset.ptcReady = 'true';

    this.variants = this.readVariants();
    this.form = this.querySelector('.ptc-pdp__form');
    this.variantInput = this.querySelector('[data-ptc-variant-id]');
    this.price = this.querySelector('[data-ptc-price]');
    this.comparePrice = this.querySelector('[data-ptc-compare-price]');
    this.addButton = this.querySelector('[data-ptc-add-button]');
    this.availability = this.querySelector('[data-ptc-availability]');
    this.mainImage = this.querySelector('.ptc-pdp__main-image');
    this.updateDeliveryDate();

    this.querySelectorAll('[data-ptc-option-input]').forEach((input) => {
      input.addEventListener('change', () => this.handleOptionChange(input));
    });

    this.querySelectorAll('[data-ptc-size-select]').forEach((select) => {
      select.addEventListener('change', () => this.handleSizeSelect(select));
    });

    this.querySelectorAll('[data-ptc-thumbnail]').forEach((button) => {
      button.addEventListener('click', () => this.selectThumbnail(button));
    });

    this.querySelector('[data-ptc-quantity-minus]')?.addEventListener('click', () => this.changeQuantity(-1));
    this.querySelector('[data-ptc-quantity-plus]')?.addEventListener('click', () => this.changeQuantity(1));
  }

  readVariants() {
    const script = this.querySelector('[data-ptc-variants]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.error('Pop the Collar: product variants could not be loaded.', error);
      return [];
    }
  }

  updateDeliveryDate() {
    const deliveryDateElement = this.querySelector('[data-ptc-delivery-date]');
    if (!(deliveryDateElement instanceof HTMLTimeElement)) return;

    const deliveryDate = new Date();
    let businessDaysAdded = 0;

    while (businessDaysAdded < 5) {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
      const dayOfWeek = deliveryDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) businessDaysAdded += 1;
    }

    const locale = document.documentElement.lang || 'en-US';
    deliveryDateElement.textContent = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(deliveryDate);
    deliveryDateElement.dateTime = [
      deliveryDate.getFullYear(),
      String(deliveryDate.getMonth() + 1).padStart(2, '0'),
      String(deliveryDate.getDate()).padStart(2, '0'),
    ].join('-');
  }

  getSelectedOptions() {
    return [...this.querySelectorAll('.ptc-pdp__option')].map((fieldset) => {
      return fieldset.querySelector('[data-ptc-option-input]:checked')?.value || '';
    });
  }

  handleOptionChange(input) {
    const fieldset = input.closest('.ptc-pdp__option');
    const selectedLabel = fieldset?.querySelector('[data-ptc-selected-option]');
    if (selectedLabel) selectedLabel.textContent = input.value;

    const options = this.getSelectedOptions();
    const variant = this.variants.find((item) => {
      return item.options.length === options.length && item.options.every((option, index) => option === options[index]);
    });

    this.updateVariant(variant);
  }

  handleSizeSelect(select) {
    const fieldset = this.querySelector(
      `.ptc-pdp__option[data-option-position="${select.dataset.optionPosition}"]`
    );
    const matchingInput = [...(fieldset?.querySelectorAll('[data-ptc-option-input]') || [])].find(
      (input) => input.value === select.value
    );

    if (!matchingInput) return;
    matchingInput.checked = true;
    this.handleOptionChange(matchingInput);
  }

  updateVariant(variant) {
    if (!variant) {
      if (this.variantInput) this.variantInput.value = '';
      if (this.addButton) {
        this.addButton.disabled = true;
        this.addButton.textContent = 'Unavailable';
      }
      if (this.availability) this.availability.textContent = 'This combination is unavailable';
      return;
    }

    if (this.variantInput) this.variantInput.value = String(variant.id);
    if (this.price) this.price.textContent = variant.price;

    if (this.comparePrice) {
      this.comparePrice.textContent = variant.comparePrice || '';
      this.comparePrice.classList.toggle('is-hidden', !variant.comparePrice);
    }

    if (this.addButton) {
      this.addButton.disabled = !variant.available;
      this.addButton.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }

    if (this.availability) {
      this.availability.innerHTML = variant.available
        ? '<span aria-hidden="true"></span> Available to order'
        : 'Sold out';
    }

    if (variant.imageUrl) {
      this.setMainImage(variant.imageUrl, variant.imageAlt || '', variant.mediaId);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  selectThumbnail(button) {
    this.setMainImage(button.dataset.imageUrl, button.dataset.imageAlt || '', button.dataset.mediaId);
  }

  setMainImage(url, alt, mediaId) {
    if (!this.mainImage || !url) return;
    // A slower previous image must not overwrite a more recent color choice.
    const request = this.imageRequest = (this.imageRequest || 0) + 1;
    this.mainImage.classList.add('is-changing');

    const preload = new Image();
    preload.onload = () => {
      if (request !== this.imageRequest) return;
      this.mainImage.src = url;
      this.mainImage.srcset = '';
      this.mainImage.alt = alt;
      this.mainImage.classList.remove('is-changing');
    };
    preload.onerror = () => {
      if (request === this.imageRequest) this.mainImage.classList.remove('is-changing');
    };
    preload.src = url;

    this.querySelectorAll('[data-ptc-thumbnail]').forEach((thumbnail) => {
      const active = String(thumbnail.dataset.mediaId) === String(mediaId);
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  changeQuantity(change) {
    const input = this.querySelector('[data-ptc-quantity]');
    if (!(input instanceof HTMLInputElement)) return;
    const minimum = Number(input.min || 1);
    const current = Number(input.value || minimum);
    input.value = String(Math.max(minimum, current + change));
  }
}

if (!customElements.get('ptc-product-page')) {
  customElements.define('ptc-product-page', PtcProductPage);
}
